/**
 * Serial dilutions and plate-count back-calculation.
 *
 * In a series every tube is made by moving V ÷ f of the previous tube (or the stock) into
 * V − V ÷ f of diluent, so each step divides the concentration by f and tube k sits at
 * f^k overall. Plate counts run the other way: CFU/mL = mean colonies × (cumulative
 * dilution) ÷ volume plated in mL.
 */

export const MAX_STEPS = 24;

export type SeriesError = "factor" | "steps" | "volume" | "start";

export interface SeriesInput {
  startConc: number | null;
  factor: number;
  steps: number;
  /** Total volume of each tube after mixing (any volume unit). */
  volume: number;
}

export interface SeriesRow {
  tube: number;
  /** Overall dilution: 10 for the first 1-in-10 tube, 100 for the second. */
  cumulative: number;
  concentration: number | null;
  /** Volume moved in, from the stock for tube 1 and from the previous tube after that. */
  transfer: number;
  diluent: number;
  /** Left in the tube once the next transfer has been taken. */
  remaining: number;
}

export interface SeriesPlan {
  rows: SeriesRow[];
  totalStock: number;
  totalDiluent: number;
}

export function buildSeries(
  input: SeriesInput
): { plan: SeriesPlan } | { error: SeriesError } {
  if (!(input.factor > 1)) {
    return { error: "factor" };
  }
  if (
    !Number.isInteger(input.steps) ||
    input.steps < 1 ||
    input.steps > MAX_STEPS
  ) {
    return { error: "steps" };
  }
  if (!(input.volume > 0)) {
    return { error: "volume" };
  }
  if (input.startConc !== null && !(input.startConc > 0)) {
    return { error: "start" };
  }
  const transfer = input.volume / input.factor;
  const diluent = input.volume - transfer;
  const rows: SeriesRow[] = [];
  for (let tube = 1; tube <= input.steps; tube += 1) {
    const cumulative = input.factor ** tube;
    rows.push({
      tube,
      cumulative,
      concentration:
        input.startConc === null ? null : input.startConc / cumulative,
      transfer,
      diluent,
      remaining: tube === input.steps ? input.volume : input.volume - transfer,
    });
  }
  return {
    plan: {
      rows,
      totalStock: transfer,
      totalDiluent: diluent * input.steps,
    },
  };
}

/** `10⁻³` for a 10-fold series, null for any other factor. */
export function tenFoldNotation(factor: number, tube: number): string | null {
  if (factor !== 10) {
    return null;
  }
  const superscripts = "⁰¹²³⁴⁵⁶⁷⁸⁹";
  let digits = "";
  for (const digit of String(tube)) {
    digits += superscripts[Number(digit)];
  }
  return `10⁻${digits}`;
}

export type CountRangeId = "textbook" | "apha" | "iso";

export const COUNT_RANGES: Record<CountRangeId, readonly [number, number]> = {
  textbook: [30, 300],
  apha: [25, 250],
  iso: [15, 300],
};

export type PlateVerdict = "low" | "ok" | "high";

export function judgePlate(count: number, range: CountRangeId): PlateVerdict {
  const [low, high] = COUNT_RANGES[range];
  if (count < low) {
    return "low";
  }
  return count > high ? "high" : "ok";
}

export type CfuError = "counts" | "cumulative" | "plated" | "predilution";

export interface CfuInput {
  counts: readonly number[];
  /** Overall dilution of the plated tube, from the series (f^k). */
  cumulative: number;
  /** Extra dilution made before tube 1, such as 1 g in 9 mL = 10. */
  preDilution: number;
  platedMl: number;
}

export interface CfuResult {
  mean: number;
  cfuPerMl: number;
  /** CFU/mL as read off each plate on its own. */
  perPlate: number[];
}

export function cfuFromCounts(
  input: CfuInput
): { result: CfuResult } | { error: CfuError } {
  if (input.counts.length === 0 || input.counts.some((c) => !(c >= 0))) {
    return { error: "counts" };
  }
  if (!(input.cumulative >= 1)) {
    return { error: "cumulative" };
  }
  if (!(input.platedMl > 0)) {
    return { error: "plated" };
  }
  if (!(input.preDilution >= 1)) {
    return { error: "predilution" };
  }
  const scale = (input.cumulative * input.preDilution) / input.platedMl;
  const mean =
    input.counts.reduce((sum, c) => sum + c, 0) / input.counts.length;
  return {
    result: {
      mean,
      cfuPerMl: mean * scale,
      perPlate: input.counts.map((c) => c * scale),
    },
  };
}
